import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { LogOut, ShieldAlert } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAdminMetrics } from '@/hooks/useAdminMetrics';
import { MetricCard } from '@/components/admin/MetricCard';

export default function DashboardPage() {
  const { t } = useTranslation('auth');
  const { signOut } = useAuth();
  const { metrics, loading, errors, refetch } = useAdminMetrics();

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-neutral-50 to-secondary-50">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-sm shadow-md sticky top-0 z-50 border-b border-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <a href="/" className="text-2xl font-display font-bold bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent hover:from-primary-600 hover:to-accent-600 transition-all">
                Suersdey
              </a>
              <span className="hidden sm:inline text-neutral-400">|</span>
              <h1 className="hidden sm:inline text-lg font-semibold text-neutral-700">
                {t('dashboard.title')}
              </h1>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-sm"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">{t('dashboard.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Metrics Grid */}
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Link to="/admin/users" className="block transition-transform hover:scale-105">
            <MetricCard
              title={t('dashboard.metrics.users')}
              metric={metrics?.users ?? null}
              loading={loading}
              error={errors.users}
              onRetry={() => refetch('users')}
            />
          </Link>
          <Link to="/admin/businesses" className="block transition-transform hover:scale-105">
            <MetricCard
              title={t('dashboard.metrics.businesses')}
              metric={metrics?.businesses ?? null}
              loading={loading}
              error={errors.businesses}
              onRetry={() => refetch('businesses')}
            />
          </Link>
          <MetricCard
            title={t('dashboard.metrics.jobs')}
            metric={metrics?.jobs ?? null}
            loading={loading}
            error={errors.jobs}
            onRetry={() => refetch('jobs')}
          />
          <Link to="/admin/marketplace" className="block transition-transform hover:scale-105">
            <MetricCard
              title={t('dashboard.metrics.marketplace')}
              metric={metrics?.marketplace ?? null}
              loading={loading}
              error={errors.marketplace}
              onRetry={() => refetch('marketplace')}
            />
          </Link>
        </div>

        {/* Admin Tools */}
        <h2 className="text-lg font-semibold text-neutral-700 mb-4">
          {t('dashboard.tools', 'Admin Tools')}
        </h2>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Link
            to="/admin/fraud"
            className="block transition-transform hover:scale-105"
          >
            <div className="rounded-lg bg-white border border-neutral-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <ShieldAlert size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {t('dashboard.fraud.title', 'Fraud Detection')}
                  </h3>
                  <p className="text-sm text-neutral-500">
                    {t('dashboard.fraud.description', 'Monitor and manage fraud alerts')}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
}
